/**
 * JigsawBoard — spatial puzzle for the Apex puzzle (hoodie -> papoose).
 *
 * A grid of slots where jigsaw pieces must be placed in the correct
 * positions. Minna's Aura Sight reveals the target pattern, and
 * Basil's Turn-Table Touch cycles pieces at each slot.
 *
 * Each slot is a flat box mesh with colored face indicating the piece.
 * Pattern overlay is toggled by showPattern()/hidePattern().
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";

export interface JigsawSlot {
  expectedPieceId: number;
  currentPieceId: number;
  localX: number;
  localY: number;
  slotMesh: Mesh;
  pieceMesh: Mesh | null;
  ghostMesh: Mesh | null;
}

export interface JigsawBoardConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  numPieces: number;
}

/** Color palette for piece distinction. */
const PIECE_COLORS: string[] = [
  "#8b4513", // Saddle brown
  "#a0522d", // Sienna
  "#654321", // Dark brown
  COLORS.COPPER,
  COLORS.BRASS,
  COLORS.EMERALD,
];

export class JigsawBoard extends InteractiveObject {
  private slots: JigsawSlot[] = [];
  private patternVisible = false;
  private _isSolved = false;
  private readonly numPieces: number;

  private readonly slotSize = 0.7;

  /**
   * Visible "hoodie papoose" prop that always sits next to the board even
   * when the board itself is hidden by Aura Sight gating. The L1 QA pass
   * flagged that the apex puzzle was a "dead zone" — invisible at world
   * X=120 unless the player happened to press Q. The prop fixes that by
   * giving the puzzle physical presence as a visible cozy cat carrier;
   * Aura Sight still reveals the actual solve mechanism (slots + pattern).
   * Loaded via AssetManager so multiple JigsawBoards in a scene share a
   * cached template. Lifetime is owned by this class — disposed in dispose().
   */
  private papooseProp: InstantiatedModel | null = null;

  constructor(scene: Scene, config: JigsawBoardConfig) {
    const numPieces = config.numPieces;
    const cols = Math.ceil(Math.sqrt(numPieces));
    const rows = Math.ceil(numPieces / cols);
    const slotSize = 0.7;

    const boardSize = new Vector3(
      cols * slotSize + 0.4,
      rows * slotSize + 0.4,
      0.15,
    );
    super(scene, { ...config, size: config.size ?? boardSize });

    this.numPieces = numPieces;

    // Board backing
    const board = MeshBuilder.CreateBox(
      `jigsaw_${config.key}_board`,
      { width: boardSize.x, height: boardSize.y, depth: boardSize.z },
      scene,
    );
    board.parent = this.rootMesh;
    board.position = Vector3.Zero();

    const boardMat = new StandardMaterial(`jigsaw_${config.key}_boardMat`, scene);
    boardMat.diffuseColor = new Color3(0.15, 0.1, 0.08);
    boardMat.specularColor = Color3.Black();
    boardMat.emissiveColor = new Color3(0.01, 0.005, 0.003);
    board.material = boardMat;
    this.material = boardMat;

    // Create slot meshes
    for (let i = 0; i < numPieces; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const lx = (col - (cols - 1) / 2) * slotSize;
      const ly = (row - (rows - 1) / 2) * slotSize;

      // Slot outline (wireframe box)
      const slotMesh = MeshBuilder.CreateBox(
        `jigsaw_${config.key}_slot_${i}`,
        { width: slotSize - 0.05, height: slotSize - 0.05, depth: 0.02 },
        scene,
      );
      slotMesh.parent = this.rootMesh;
      slotMesh.position = new Vector3(lx, ly, boardSize.z / 2 + 0.015);

      const slotMat = new StandardMaterial(`jigsawSlot_${i}_mat`, scene);
      slotMat.diffuseColor = Color3.FromHexString(COLORS.COPPER);
      slotMat.alpha = 0.4;
      slotMat.wireframe = true;
      slotMat.specularColor = Color3.Black();
      slotMesh.material = slotMat;

      this.slots.push({
        expectedPieceId: i,
        currentPieceId: -1,
        localX: lx,
        localY: ly,
        slotMesh,
        pieceMesh: null,
        ghostMesh: null,
      });
    }

    // Fire-and-forget load of the visible papoose prop. Parented to the
    // scene root (NOT this.rootMesh) so it survives the InteractiveObject
    // hide/reveal toggle and stays visible even when the puzzle is gated
    // behind Aura Sight. Positioned just above the board so it reads as
    // "the cozy carrier you're trying to weave."
    this.loadPapooseProp();
  }

  private async loadPapooseProp(): Promise<void> {
    try {
      const instance = await getAssetManager().instantiateSharedModel(
        this.scene,
        "/assets/models/environment/hoodie_papoose.glb",
      );
      this.papooseProp = instance;
      const boardWorld = this.rootMesh.position;
      for (const root of instance.rootNodes) {
        if ("position" in root && "scaling" in root) {
          (root as { position: Vector3; scaling: Vector3 }).position =
            new Vector3(boardWorld.x, boardWorld.y + 1.4, boardWorld.z);
          (root as { position: Vector3; scaling: Vector3 }).scaling = new Vector3(0.9, 0.9, 0.9);
        }
      }
      for (const mesh of instance.meshes) {
        mesh.isPickable = false;
      }
    } catch (e) {
      console.warn("[JigsawBoard] Failed to load hoodie_papoose.glb prop:", e);
    }
  }

  /** Show the target pattern (via Aura Sight). */
  showPattern(): void {
    if (this.patternVisible) return;
    this.patternVisible = true;

    for (const slot of this.slots) {
      if (!slot.ghostMesh) {
        const ghost = MeshBuilder.CreateBox(
          `jigsawGhost_${this.key}_${slot.expectedPieceId}`,
          { width: this.slotSize - 0.1, height: this.slotSize - 0.1, depth: 0.01 },
          this.scene,
        );
        ghost.parent = this.rootMesh;
        ghost.position = new Vector3(
          slot.localX,
          slot.localY,
          this.config.size.z / 2 + 0.03,
        );

        const gMat = new StandardMaterial(
          `jigsawGhostMat_${slot.expectedPieceId}`,
          this.scene,
        );
        const color = Color3.FromHexString(
          PIECE_COLORS[slot.expectedPieceId % PIECE_COLORS.length],
        );
        gMat.diffuseColor = color;
        gMat.emissiveColor = color.scale(0.2);
        gMat.alpha = 0.25;
        gMat.specularColor = Color3.Black();
        ghost.material = gMat;
        slot.ghostMesh = ghost;
      }
      slot.ghostMesh.isVisible = true;
    }
  }

  /** Hide the target pattern. */
  hidePattern(): void {
    if (!this.patternVisible) return;
    this.patternVisible = false;

    for (const slot of this.slots) {
      if (slot.ghostMesh) {
        slot.ghostMesh.isVisible = false;
      }
    }
  }

  /**
   * Place a piece at the nearest empty slot to a world position.
   * Returns true if the piece was placed.
   */
  placePiece(pieceId: number, worldPos: Vector3): boolean {
    const local = worldPos.subtract(this.rootMesh.position);
    let nearest: JigsawSlot | null = null;
    let nearestDist = Infinity;

    for (const slot of this.slots) {
      if (slot.currentPieceId !== -1) continue;
      const dist = Math.sqrt(
        (slot.localX - local.x) ** 2 + (slot.localY - local.y) ** 2,
      );
      if (dist < nearestDist && dist < 1.5) {
        nearestDist = dist;
        nearest = slot;
      }
    }

    if (!nearest) return false;

    nearest.currentPieceId = pieceId;
    this.updateSlotVisual(nearest);
    this.checkSolved();
    return true;
  }

  /**
   * Cycle the next-best slot when the cat presses interact near the
   * board.
   *
   * Selection priority:
   *   1. Lowest-index UNSOLVED slot whose currentPieceId hasn't yet
   *      reached its expectedPieceId. Each press advances that slot
   *      by one step (-1 → 0 → 1 → ... → expected). Once a slot
   *      matches its expectation, it's skipped on subsequent presses.
   *   2. If every slot is solved, no-op.
   *
   * This replaces the prior "nearest filled slot" behavior, which
   * required the player to precisely position the cat near each slot
   * AND for each slot to already be filled — but `placePiece` was
   * never wired to gameplay, so the board could never make progress.
   * The new behavior is a single deterministic cycle order: stand near
   * the board and tap interact 1+expected times per slot until solved.
   *
   * The proximity gate (cat within 1.5u of any slot OR 1.5u of board
   * center) still keeps incidental presses from progressing the puzzle
   * when the cat is far away.
   */
  cycleSlot(worldPos: Vector3): boolean {
    const local = worldPos.subtract(this.rootMesh.position);

    // Proximity gate — must be within 1.5u of some slot.
    let inRange = false;
    for (const slot of this.slots) {
      const dist = Math.sqrt(
        (slot.localX - local.x) ** 2 + (slot.localY - local.y) ** 2,
      );
      if (dist < 1.5) {
        inRange = true;
        break;
      }
    }
    if (!inRange) return false;

    // Find the lowest-index slot that doesn't yet match its expected
    // piece. Advance it by one step.
    for (const slot of this.slots) {
      if (slot.currentPieceId === slot.expectedPieceId) continue;
      if (slot.currentPieceId < 0) {
        slot.currentPieceId = 0;
      } else {
        slot.currentPieceId = (slot.currentPieceId + 1) % this.numPieces;
      }
      this.updateSlotVisual(slot);
      this.checkSolved();
      return true;
    }

    // All slots solved — nothing to cycle.
    return false;
  }

  get isSolved(): boolean {
    return this._isSolved;
  }

  private updateSlotVisual(slot: JigsawSlot): void {
    // Remove old piece mesh
    if (slot.pieceMesh) {
      slot.pieceMesh.material?.dispose();
      slot.pieceMesh.dispose();
      slot.pieceMesh = null;
    }

    if (slot.currentPieceId < 0) return;

    // Create piece mesh
    const piece = MeshBuilder.CreateBox(
      `jigsawPiece_${this.key}_${slot.expectedPieceId}`,
      { width: this.slotSize - 0.12, height: this.slotSize - 0.12, depth: 0.03 },
      this.scene,
    );
    piece.parent = this.rootMesh;
    piece.position = new Vector3(
      slot.localX,
      slot.localY,
      this.config.size.z / 2 + 0.04,
    );

    const color = Color3.FromHexString(
      PIECE_COLORS[slot.currentPieceId % PIECE_COLORS.length],
    );
    const isCorrect = slot.currentPieceId === slot.expectedPieceId;

    const pMat = new StandardMaterial(
      `jigsawPieceMat_${slot.currentPieceId}`,
      this.scene,
    );
    pMat.diffuseColor = color;
    pMat.emissiveColor = isCorrect
      ? Color3.FromHexString(COLORS.EMERALD).scale(0.2)
      : color.scale(0.05);
    pMat.alpha = isCorrect ? 0.95 : 0.75;
    pMat.specularColor = Color3.Black();
    piece.material = pMat;
    slot.pieceMesh = piece;
  }

  private checkSolved(): void {
    const allCorrect = this.slots.every(
      (slot) => slot.currentPieceId === slot.expectedPieceId,
    );

    if (allCorrect && !this._isSolved) {
      this._isSolved = true;
      this.solve();
      gameEvents.emit(GameEvents.JIGSAW_BOARD_SOLVED, this.key);
    }
  }

  override dispose(): void {
    if (this.papooseProp) {
      this.papooseProp.dispose();
      this.papooseProp = null;
    }
    super.dispose();
  }
}
